import { Entity, WatchTower } from "@eai-toolkit-material-ui/icons";
import { Cloud, Person } from "@mui/icons-material";
import { Autocomplete, Box, InputAdornment, TextField } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";

import ResourceSelector from "../../../../components/ResourceSelector/ResourceSelector";
import { RESOURCE_NAME_FIELD } from "../../../../store/api/resourceTypes";
import type { ResourceType } from "../../../../store/api/resourceTypes";
import type { SelectedResource } from "../data/types";

type ResourceTypeOption = {
  value: ResourceType;
  label: string;
  icon: React.ElementType;
};

const RESOURCE_TYPE_OPTIONS: ResourceTypeOption[] = [
  { value: "data", label: "Data", icon: Entity },
  { value: "job", label: "Job", icon: WatchTower },
  { value: "service", label: "Service", icon: Cloud },
  { value: "account", label: "Account", icon: Person },
];

const TYPE_COLUMN_WIDTH = 200;
const COLUMN_GAP = 2;

const SelectorRow = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(COLUMN_GAP),
}));

// TODO: May want to rename this component to something more clear like ResourceTypeAndNamePicker.
export type ResourcePickerProps = {
  // Separate from value.type because resourceType drives the search even when no resource is selected yet
  resourceType: ResourceType;
  value: SelectedResource | null;
  onResourceTypeChange: (resourceType: ResourceType) => void;
  onChange: (resource: SelectedResource | null) => void;
  // Optional caption rendered under the search field, aligned with it.
  helperText?: React.ReactNode;
};

function ResourcePicker({
  resourceType,
  value,
  onResourceTypeChange,
  onChange,
  helperText,
}: ResourcePickerProps) {
  const resourceTypeOption =
    RESOURCE_TYPE_OPTIONS.find((opt) => opt.value === resourceType) ??
    RESOURCE_TYPE_OPTIONS[0];

  const handleResourceTypeChange = (
    _event: React.SyntheticEvent,
    newValue: ResourceTypeOption | null,
  ) => {
    if (newValue) {
      onResourceTypeChange(newValue.value);
      onChange(null);
    }
  };

  const nameField = RESOURCE_NAME_FIELD[resourceType];
  const selectedStub =
    value?.type === resourceType
      ? {
          id: value.id,
          [nameField]: value.name,
          account: value.account,
          organization: value.organization,
          urn: value.urn,
        }
      : null;

  return (
    <Box>
      <SelectorRow>
        <Autocomplete
          sx={{ width: TYPE_COLUMN_WIDTH }}
          options={RESOURCE_TYPE_OPTIONS}
          value={resourceTypeOption}
          onChange={handleResourceTypeChange}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, val) => option.value === val.value}
          disableClearable
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            const Icon = option.icon;
            return (
              <li key={key} {...rest}>
                <Icon fontSize="small" sx={{ mr: 1 }} />
                {option.label}
              </li>
            );
          }}
          renderInput={(params) => {
            const Icon = resourceTypeOption.icon;
            return (
              <TextField
                {...params}
                label="Resource type"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            );
          }}
        />
        <ResourceSelector
          key={resourceType}
          sx={{ flex: 1 }}
          resourceType={resourceType}
          metadataFallback
          value={selectedStub}
          onChange={(item) =>
            onChange(
              item
                ? {
                    id: item.id ?? "",
                    name: item[nameField] ?? item.fullName ?? item.id ?? "",
                    type: resourceType,
                    account: item.account,
                    organization: item.organization,
                    urn: item.urn,
                  }
                : null,
            )
          }
        />
      </SelectorRow>
      {helperText && (
        <Box sx={{ display: "flex", gap: COLUMN_GAP, mt: 1 }}>
          <Box sx={{ width: TYPE_COLUMN_WIDTH, flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>{helperText}</Box>
        </Box>
      )}
    </Box>
  );
}

export default ResourcePicker;
